import axios from 'axios'
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import {
  HISTOGRAM_BOUNDS_MEDIUM,
  OpenTelemetryManager,
  TgtLogger,
  TgtMeter,
  TgtTracer,
} from '@measurement/opentelemetry-web'
import type { OpenTelemetryConfig } from '@measurement/opentelemetry-web'
import apiConfig from '../config/apiConfig'

/**
 * Centralized OpenTelemetry (OTel) setup for the RIK UI.
 *
 * Responsibilities:
 *  - Initialize the Target `OpenTelemetryManager` exactly once (idempotent).
 *  - Register axios interceptors that emit request count + latency metrics
 *    for every API call the app makes.
 *  - Provide small, safe helpers (`incrementCounter`, `recordHistogram`) so
 *    feature code can emit custom business metrics.
 *
 * Telemetry must NEVER break the app, so every public entry point is wrapped
 * in defensive guards / try-catch.
 */

type MetricAttributes = Record<string, string | number | boolean>

/**
 * Minimal shape of the OpenTelemetry `Span` returned by `TgtTracer(...)`.
 * We only rely on the small surface the app actually uses so feature code
 * never has to import the OTel API types directly.
 */
export interface OtelSpan {
  end: () => void
  setStatus?: (status: { code: number; message?: string }) => void
  setAttribute?: (key: string, value: string | number | boolean) => void
  setAttributes?: (attributes: MetricAttributes) => void
  recordException?: (exception: unknown) => void
}

/**
 * OTel `SpanStatusCode` values (mirrors the `@opentelemetry/api` enum) so we
 * can mark spans as errored without importing the library type.
 */
const SPAN_STATUS_ERROR = 2

/**
 * Valid `environment` values we tag telemetry with. This attribute is attached
 * as a resource tag to every span/metric/log, and is how telemetry is
 * segmented per-environment in the TAP dashboards — the API key is always a
 * prod key and does NOT differentiate environments.
 */
type OtelEnvironment = 'local' | 'wip' | 'stage' | 'prod'

/**
 * Map the app's `appEnv` value (from the environment config) onto the OTel
 * `environment` resource tag. Only `'local' | 'wip' | 'stage' | 'prod'` are
 * supported; anything unrecognised falls back to `'wip'` so we never tag
 * telemetry with an invalid value (and never accidentally tag it as `prod`).
 */
const resolveOtelEnvironment = (appEnv?: string): OtelEnvironment => {
  switch ((appEnv || '').toLowerCase()) {
    case 'local':
      return 'local'
    case 'wip':
      return 'wip'
    case 'stg':
    case 'dev':
    case 'stage':
      return 'stage'
    case 'prod':
      return 'prod'
    default:
      return 'wip'
  }
}

let initialized = false
let axiosInstrumented = false

// Cache instruments by name — OTel instruments should be created once and reused.
const counters: Record<string, ReturnType<typeof TgtMeter.createCounter>> = {}
const histograms: Record<
  string,
  ReturnType<typeof TgtMeter.createHistogram>
> = {}

/**
 * Initialize OpenTelemetry once for the whole application.
 * Safe to call multiple times — subsequent calls are no-ops.
 */
export const initializeOpenTelemetry = (): void => {
  if (initialized) {
    return
  }

  const otel = (apiConfig as { otelConfig?: Partial<OpenTelemetryConfig> })
    .otelConfig
  const apiKey = otel?.apiKey
  const serviceName = otel?.serviceName || 'rik'
  // Derive the deployment environment so all telemetry is tagged for
  // per-environment filtering in TAP. `appEnv` is flattened onto apiConfig
  // from each env config's `commonConfig` (e.g. 'dev' | 'stg' | 'prod').
  const environment = resolveOtelEnvironment(
    (apiConfig as { appEnv?: string }).appEnv,
  )

  // Without an API key the web exporter cannot send data; skip init rather
  // than throwing during app bootstrap.
  if (!apiKey) {
    return
  }

  try {
    const config: OpenTelemetryConfig = {
      apiKey,
      serviceName,
      // The library's `WebEnvironment` type only lists 'dev' (not 'wip'), but
      // `environment` is just a free-form `deployment.environment` resource
      // tag at runtime. Cast so we can keep our preferred 'wip' label.
      environment: environment as OpenTelemetryConfig['environment'],
      // Capture uncaught errors / unhandled rejections as OTel telemetry
      // automatically (in addition to our React ErrorBoundary counter).
      enableGlobalErrorHandler: true,
    }
    OpenTelemetryManager.initialize(config)
    initialized = true
    instrumentAxios()
  } catch {
    // Swallow: a telemetry failure should never prevent the app from loading.
  }
}

const getCounter = (name: string, description: string) => {
  if (!counters[name]) {
    counters[name] = TgtMeter.createCounter(name, { description })
  }
  return counters[name]
}

const getHistogram = (name: string, description: string) => {
  if (!histograms[name]) {
    histograms[name] = TgtMeter.createHistogram(name, {
      description,
      advice: { explicitBucketBoundaries: HISTOGRAM_BOUNDS_MEDIUM },
    })
  }
  return histograms[name]
}

/**
 * Emit (or increment) a custom OTel counter. No-op until OTel is initialized.
 */
export const incrementCounter = (
  name: string,
  description: string,
  value = 1,
  attributes: MetricAttributes = {},
): void => {
  if (!initialized) {
    return
  }
  try {
    getCounter(name, description).add(value, attributes)
  } catch {
    // ignore telemetry errors
  }
}

/**
 * Record a value into a custom OTel histogram. No-op until OTel is initialized.
 */
export const recordHistogram = (
  name: string,
  description: string,
  value: number,
  attributes: MetricAttributes = {},
): void => {
  if (!initialized) {
    return
  }
  try {
    getHistogram(name, description).record(value, attributes)
  } catch {
    // ignore telemetry errors
  }
}

/**
 * Reduce a request URL to a low-cardinality label (pathname only, no query
 * string or origin) so metric series don't explode.
 */
const getEndpointLabel = (url?: string): string => {
  if (!url) {
    return 'unknown'
  }
  try {
    return new URL(url, window.location.origin).pathname
  } catch {
    return url.split('?')[0]
  }
}

const recordApiMetrics = (
  config:
    | (AxiosRequestConfig & { metadata?: { startTime: number } })
    | undefined,
  status: number,
  isError = false,
): void => {
  const startTime = config?.metadata?.startTime
  const attributes: MetricAttributes = {
    endpoint: getEndpointLabel(config?.url),
    method: (config?.method || 'get').toUpperCase(),
    status: String(status),
    outcome: isError || status >= 400 ? 'error' : 'success',
  }

  incrementCounter(
    'rik_api_requests_total',
    'Total number of API requests made by the RIK UI',
    1,
    attributes,
  )

  if (typeof startTime === 'number') {
    recordHistogram(
      'rik_api_request_duration_ms',
      'Duration of RIK UI API requests in milliseconds',
      performance.now() - startTime,
      attributes,
    )
  }
}

/**
 * Register global axios interceptors that time every request and emit
 * count + latency metrics. Idempotent.
 */
export const instrumentAxios = (): void => {
  if (axiosInstrumented) {
    return
  }
  axiosInstrumented = true

  axios.interceptors.request.use((config) => {
    ;(
      config as AxiosRequestConfig & { metadata?: { startTime: number } }
    ).metadata = { startTime: performance.now() }
    return config
  })

  axios.interceptors.response.use(
    (response: AxiosResponse) => {
      recordApiMetrics(response.config, response.status)
      return response
    },
    (error: AxiosError) => {
      recordApiMetrics(error.config, error.response?.status ?? 0, true)
      return Promise.reject(error)
    },
  )
}

/* -------------------------------------------------------------------------- */
/* High-level semantic helpers for feature instrumentation.                   */
/* These are thin, safe wrappers around the primitives above so feature code  */
/* never has to know metric names or attribute conventions.                   */
/* -------------------------------------------------------------------------- */

/**
 * Record a page view / route navigation. Use the route pattern (not a value
 * with ids) where possible to keep cardinality low.
 */
export const trackPageView = (path: string): void => {
  incrementCounter(
    'rik_page_views_total',
    'Total number of page views / route navigations in the RIK UI',
    1,
    { path: path || 'unknown' },
  )
}

/**
 * Record a user-initiated event (clicks, logon/logout, etc). Mirrors the
 * existing analytics `trackEvent` calls so OTel stays in sync with Firefly.
 */
export const trackUserEvent = (
  eventType: string,
  attributes: MetricAttributes = {},
): void => {
  incrementCounter(
    'rik_user_events_total',
    'Total number of user-initiated events in the RIK UI',
    1,
    { event: eventType || 'unknown', ...attributes },
  )
}

/**
 * Record a feature/business action (e.g. report export, schedule created,
 * view saved). `status` distinguishes success vs failure.
 */
export const trackFeatureAction = (
  action: string,
  status: 'success' | 'error' | 'attempt' = 'attempt',
  attributes: MetricAttributes = {},
): void => {
  incrementCounter(
    'rik_feature_actions_total',
    'Total number of feature/business actions performed in the RIK UI',
    1,
    { action, status, ...attributes },
  )

  // Mirror the `attempt → success/error` lifecycle as a trace span so every
  // instrumented workflow is *traced* as well as counted, with no extra code
  // at the call sites. Safe no-op until OTel is initialized.
  if (status === 'attempt') {
    startFeatureSpan(action, attributes)
  } else if (status === 'error') {
    endFeatureSpan(action, true)
  } else {
    endFeatureSpan(action)
  }
}

/**
 * Record a frontend error (caught by an error boundary or global handler).
 */
export const trackError = (
  source: string,
  attributes: MetricAttributes = {},
): void => {
  incrementCounter(
    'rik_frontend_errors_total',
    'Total number of frontend errors captured in the RIK UI',
    1,
    { source: source || 'unknown', ...attributes },
  )
}

/* -------------------------------------------------------------------------- */
/* Tracing helpers (custom spans).                                            */
/*                                                                            */
/* These wrap the library's `TgtTracer` to create custom spans that time      */
/* specific workflows (e.g. report export, view save). They follow the same   */
/* "never break the app / no-op until initialized" contract as the metric     */
/* helpers above, so feature code can call them unconditionally.              */
/* -------------------------------------------------------------------------- */

/**
 * Prefix span names so they're easy to find/filter alongside the `rik_`
 * metrics in the TAP/Grafana trace explorer.
 */
const SPAN_PREFIX = 'rik.'

/**
 * Registry of in-flight "feature" spans, keyed by action name. The feature
 * lifecycle (`trackFeatureAction`) is split across separate `attempt` and
 * `success`/`error` calls, so the span started on `attempt` is stashed here
 * and retrieved when the workflow completes.
 *
 * Each entry is a small stack so that overlapping/concurrent runs of the same
 * action (e.g. two exports at once) are paired LIFO and never leak.
 */
const featureSpans: Record<string, OtelSpan[]> = {}

/**
 * Begin a trace span for a feature workflow `attempt`. Internal — driven by
 * {@link trackFeatureAction}; feature code never calls this directly.
 */
const startFeatureSpan = (
  action: string,
  attributes: MetricAttributes = {},
): void => {
  const span = startSpan(action, { action, ...attributes })
  if (!span) {
    return
  }
  if (!featureSpans[action]) {
    featureSpans[action] = []
  }
  featureSpans[action].push(span)
}

/**
 * Complete the most recent trace span for a feature workflow
 * (`success`/`error`). Internal — driven by {@link trackFeatureAction}.
 */
const endFeatureSpan = (action: string, isError = false): void => {
  const span = featureSpans[action]?.pop()
  if (!span) {
    return
  }
  if (isError) {
    failSpan(span)
  } else {
    endSpan(span)
  }
}

/**
 * Start a custom span. Returns the span (so callers can `end()` it) or
 * `undefined` if OTel isn't initialized or span creation failed — callers
 * must always null-check, or simply use {@link withSpan} instead.
 *
 * @example
 * const span = startSpan('report_export', { export_format: 'CSV' })
 * try { ...work... } finally { endSpan(span) }
 */
export const startSpan = (
  name: string,
  attributes: MetricAttributes = {},
): OtelSpan | undefined => {
  if (!initialized) {
    return undefined
  }
  try {
    // `TgtTracer` (v5+) is a class exposing static span factories; `startSpan`
    // returns a standard OTel `Span` (`.end()`, `.setStatus()`, etc.).
    return TgtTracer.startSpan(`${SPAN_PREFIX}${name}`, {
      attributes,
    }) as unknown as OtelSpan
  } catch {
    // Telemetry must never break the app.
    return undefined
  }
}

/**
 * Mark a span as failed (sets ERROR status + records the exception) and end
 * it. Safe to call with an `undefined` span.
 */
export const failSpan = (span: OtelSpan | undefined, error?: unknown): void => {
  if (!span) {
    return
  }
  try {
    span.setStatus?.({
      code: SPAN_STATUS_ERROR,
      message: error instanceof Error ? error.message : undefined,
    })
    if (error !== undefined) {
      span.recordException?.(error)
    }
  } catch {
    // ignore telemetry errors
  } finally {
    endSpan(span)
  }
}

/**
 * End a span. Safe to call with an `undefined` span (no-op).
 */
export const endSpan = (span: OtelSpan | undefined): void => {
  if (!span) {
    return
  }
  try {
    span.end()
  } catch {
    // ignore telemetry errors
  }
}

/**
 * Run an (optionally async) function wrapped in a custom span. The span is
 * automatically ended; if the function throws/rejects, the span is marked as
 * errored and the original error is re-thrown. Telemetry never alters the
 * function's result or error.
 *
 * @example
 * return withSpan('report_export', () => doExport(payload), {
 *   export_format: 'CSV',
 * })
 */
export const withSpan = async <T>(
  name: string,
  fn: () => Promise<T> | T,
  attributes: MetricAttributes = {},
): Promise<T> => {
  const span = startSpan(name, attributes)
  try {
    const result = await fn()
    endSpan(span)
    return result
  } catch (error) {
    failSpan(span, error)
    throw error
  }
}

/* -------------------------------------------------------------------------- */
/* Logging helpers (OTel logs pillar).                                        */
/*                                                                            */
/* These wrap the library's `TgtLogger` so app logs flow into the OTel logs   */
/* pipeline and are correlated with traces/metrics (shared trace_id/span_id   */
/* + the same resource tags incl. `deployment.environment`). They follow the  */
/* same "never break the app / no-op until initialized" contract as the       */
/* metric & tracing helpers above.                                            */
/* -------------------------------------------------------------------------- */

type OtelLogLevel = 'debug' | 'info' | 'warn' | 'error'

/**
 * Wrap freeform, low-cardinality attributes into the TCS log schema's
 * `nonIndexedFields` bucket (the schema-sanctioned place for arbitrary
 * key/values) so they ride along with the log record.
 */
const toLogAttributes = (attributes: MetricAttributes = {}) =>
  Object.keys(attributes).length ? { nonIndexedFields: attributes } : undefined

/**
 * Emit a structured log to the OTel logs pipeline. No-op until OTel is
 * initialized and never throws — telemetry must never break the app.
 *
 * Prefer the typed wrappers (`logInfo`, `logWarn`, `logError`, `logDebug`).
 */
export const trackLog = (
  level: OtelLogLevel,
  message: string,
  attributes: MetricAttributes = {},
): void => {
  if (!initialized) {
    return
  }
  try {
    const logAttributes = toLogAttributes(attributes)
    switch (level) {
      case 'debug':
        TgtLogger.debug(message, logAttributes)
        break
      case 'info':
        TgtLogger.info(message, logAttributes)
        break
      case 'warn':
        TgtLogger.warn(message, logAttributes)
        break
      case 'error':
        TgtLogger.error(message, logAttributes)
        break
    }
  } catch {
    // ignore telemetry errors
  }
}

/** Emit an `info`-level OTel log. No-op until initialized. */
export const logInfo = (
  message: string,
  attributes: MetricAttributes = {},
): void => trackLog('info', message, attributes)

/** Emit a `warn`-level OTel log. No-op until initialized. */
export const logWarn = (
  message: string,
  attributes: MetricAttributes = {},
): void => trackLog('warn', message, attributes)

/** Emit an `error`-level OTel log. No-op until initialized. */
export const logError = (
  message: string,
  attributes: MetricAttributes = {},
): void => trackLog('error', message, attributes)

/** Emit a `debug`-level OTel log. No-op until initialized. */
export const logDebug = (
  message: string,
  attributes: MetricAttributes = {},
): void => trackLog('debug', message, attributes)
